import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { chunkIds, CLIENT_ID_RESOLUTION_BATCH_SIZE } from '../lib/chunk-ids.js';

export interface ResolvedByIds<T extends { id: string }> {
  /** The rows the account could resolve, or null while none have been read (in flight, failed, or nothing to ask for). */
  rows: readonly T[] | null;
  /** Id to row, derived from `rows`. */
  byId: Map<string, T>;
  /** True while the read is in flight, and false when there is nothing to read. */
  isPending: boolean;
  isError: boolean;
}

interface ResolveByIdsSource<T> {
  /** The cache namespace for the row type; `'resolved'` and the ids are appended. */
  queryKey: (accountId: string) => readonly unknown[];
  list: (accountId: string, idsChunk: string[]) => Promise<{ data: T[] }>;
}

/** Null and undefined ids are skipped, and the rest deduplicated and sorted, so two holders of the same set share one cache entry. */
export function normaliseIds(ids: readonly (string | null | undefined)[]): string[] {
  return [...new Set(ids.filter((id): id is string => !!id))].sort();
}

/**
 * The batched read behind `useResolvedByIds`, split into chunks of
 * `CLIENT_ID_RESOLUTION_BATCH_SIZE` because the backend's pagination `limit`
 * caps a single call. `uniqueSortedIds` must come from `normaliseIds`.
 */
export function resolvedByIdsQueryOptions<T extends { id: string }>(
  accountId: string,
  uniqueSortedIds: string[],
  { queryKey, list }: ResolveByIdsSource<T>,
) {
  return queryOptions({
    queryKey: [...queryKey(accountId), 'resolved', uniqueSortedIds],
    queryFn: async () => {
      const responses = await Promise.all(
        chunkIds(uniqueSortedIds, CLIENT_ID_RESOLUTION_BATCH_SIZE).map((idsChunk) => list(accountId, idsChunk)),
      );
      return responses.flatMap((response) => response.data);
    },
  });
}

/**
 * The shared core of the "resolve the ids a surface holds" hooks
 * (`useResolvedClients`, `useResolvedClientProjects`): one batched read for the
 * whole set. `list` must include archived rows: an id held by another record
 * stays valid after its row is archived.
 */
export function useResolvedByIds<T extends { id: string }>(
  accountId: string | null | undefined,
  ids: readonly (string | null | undefined)[],
  source: ResolveByIdsSource<T>,
): ResolvedByIds<T> {
  const uniqueSortedIds = useMemo(() => normaliseIds(ids), [ids]);

  const enabled = !!accountId && uniqueSortedIds.length > 0;

  const { data, isPending, isError } = useQuery({
    ...resolvedByIdsQueryOptions(accountId ?? '', uniqueSortedIds, source),
    enabled,
  });

  const rows = data ?? null;

  const byId = useMemo(() => {
    const resolved = new Map<string, T>();
    for (const row of rows ?? []) resolved.set(row.id, row);
    return resolved;
  }, [rows]);

  // A disabled query reports `isPending` forever, which would leave a caller
  // holding a loading state that never resolves when there is nothing to read.
  return { rows, byId, isPending: enabled && isPending, isError };
}
